import Link from 'next/link'
import { useRouter } from 'next/router'
import { FunctionComponent } from 'react'
import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
  useTheme,
} from 'styled-components'
import FacebookIcon from '@/components/icon/billboard/top100/facebook-brand.svg'
import FacebookInactiveIcon from '@/components/icon/billboard/top100/facebook-inactive.svg'
import InstagramIcon from '@/components/icon/billboard/top100/instagram-brand.svg'
import InstagramInactiveIcon from '@/components/icon/billboard/top100/instagram-inactive.svg'
import YoutubeIcon from '@/components/icon/billboard/top100/youtube-brand.svg'
import YoutubeInactiveIcon from '@/components/icon/billboard/top100/youtube-inactive.svg'
import horizontalBackgroundSrc from '@/public/assets/billboard/horizontal-background.png'
import { RankingPlatformType } from '@/types/schema/k6/analyze-api'
import { platformShortcodeSchema } from '@/types/schema/platform/platform-shortcode-schema'

interface HeaderProps {
  title: string
  description: string
  platform: RankingPlatformType
}

const Header: FunctionComponent<HeaderProps> = ({
  title,
  description,
  platform,
}) => {
  const router = useRouter()
  const theme = useTheme()
  const getLink = (targetPlatform: string): string => {
    const fromPlatform = ((): string => {
      switch (platform) {
        case platformShortcodeSchema.enum.ig:
          return 'instagram'
        case platformShortcodeSchema.enum.fb:
          return 'facebook'
        case platformShortcodeSchema.enum.yt:
          return 'youtube'
      }
    })()
    return router.asPath.replace(fromPlatform, targetPlatform)
  }

  return (
    <HeaderBackground>
      <Wrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <LinkContainer>
          <LinkWrapper active={platform === platformShortcodeSchema.enum.ig}>
            <Link href={getLink('instagram')}>
              {platform === platformShortcodeSchema.enum.ig ? (
                <InstagramIcon />
              ) : (
                <InstagramInactiveIcon color={theme.colors.text.tooltip} />
              )}
              Instagram TOP 100
            </Link>
          </LinkWrapper>
          <LinkWrapper active={platform === platformShortcodeSchema.enum.fb}>
            <Link href={getLink('facebook')}>
              {platform === platformShortcodeSchema.enum.fb ? (
                <FacebookIcon />
              ) : (
                <FacebookInactiveIcon color={theme.colors.text.tooltip} />
              )}
              Facebook TOP 100
            </Link>
          </LinkWrapper>
          <LinkWrapper active={platform === platformShortcodeSchema.enum.yt}>
            <Link href={getLink('youtube')}>
              {platform === platformShortcodeSchema.enum.yt ? (
                <YoutubeIcon />
              ) : (
                <YoutubeInactiveIcon color={theme.colors.text.tooltip} />
              )}
              YouTube TOP 100
            </Link>
          </LinkWrapper>
        </LinkContainer>
      </Wrapper>
    </HeaderBackground>
  )
}

const Title = styled.h2`
  font-size: 36px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const Description = styled.div`
  font-size: 20px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const LinkWrapper = styled.span<{ active?: boolean }>`
  a {
    display: flex;
    align-items: center;
    font-size: 16px;
    line-height: 1.4;
    padding: 12px 20px;
    border-radius: 30px;

    @media (max-width: 768px) {
      font-size: 14px;
      padding: 12px 16px;
    }

    &:hover {
      color: ${(props): string => props.theme.colors.text.secondary};
    }

    ${({ active, theme }): FlattenInterpolation<ThemeProps<DefaultTheme>> =>
      active
        ? css`
            color: ${theme.colors.text.primary};
            background-color: ${theme.colors.text.white};
          `
        : css`
            cursor: pointer;

            @media (max-width: 768px) {
              font-size: 0;
            }
          `}
    svg {
      margin-right: 8px;
      height: 24px;
      width: 24px;
    }
  }
`

const LinkContainer = styled.div`
  display: flex;
  justify-content: center;
`

const Wrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 16px 40px;
  line-height: 1.4;
  text-align: center;

  ${Title} {
    margin-bottom: 24px;
  }

  ${Description} {
    margin-bottom: 40px;
  }
`

const HeaderBackground = styled.div`
  background-image: url(${horizontalBackgroundSrc});
  background-position: center;
  background-size: cover;
`

export default Header
